import { SITE } from '@/lib/constants';
import { buildCanonical, buildTitle, type PageSeo } from '@/lib/seo';

interface SEOHeadProps {
  seo: PageSeo;
}

/**
 * Per-page SEO block using React 19's native <title>/<meta>/<link> hoisting
 * (no react-helmet-async needed). Tags declared here are automatically
 * deduped and moved to <head> by React.
 *
 * Usage (inside a page component):
 *   <SEOHead seo={SEO_CONFIG.home} />
 */
export function SEOHead({ seo }: SEOHeadProps) {
  const title = buildTitle(seo);
  const canonical = buildCanonical(seo);
  const ogImage = seo.ogImage
    ? seo.ogImage.startsWith('http')
      ? seo.ogImage
      : `${SITE.url.replace(/\/$/, '')}${seo.ogImage}`
    : undefined;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={canonical} />

      {seo.noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="ARCOLL TECH" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={SITE.locale} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={seo.description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}
